import { Dropdown, Field, Input, Option, Textarea, makeStyles, tokens } from '@fluentui/react-components';

import { MOCK_PRODUCTS } from '../../data';
import {
  TEMPLATE_CREATION_INPUT_TYPE,
  TTemplateCreationInputConfig,
  TTemplateEntityReference,
  TTemplateInputValue,
} from '../../models';
import { isInputProvided } from '../../utils';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalM,
    '@media (max-width: 640px)': { gridTemplateColumns: '1fr' },
  },
  // Long-text inputs read better across the full width of the form.
  fullWidth: { gridColumn: '1 / -1' },
});

type TProps = {
  inputs: TTemplateCreationInputConfig[];
  values: Record<string, TTemplateInputValue>;
  onChange: (id: string, value: TTemplateInputValue) => void;
  touched: Record<string, boolean>;
  onBlur: (id: string) => void;
  // Forces every required-but-empty field to show its error (e.g. after a failed
  // Create draft), independent of whether the field was touched.
  showAllErrors: boolean;
};

const asText = (value: TTemplateInputValue | undefined): string => (typeof value === 'string' ? value : '');
const asEntityReferences = (value: TTemplateInputValue | undefined): TTemplateEntityReference[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'object')
    ? (value as TTemplateEntityReference[])
    : [];

// A compact, responsive form for the template-specific facts. It never becomes the
// full request form: only the few inputs a template declares are collected here.
export const TemplateSetupForm = ({ inputs, values, onChange, touched, onBlur, showAllErrors }: TProps) => {
  const classes = useStyles();

  return (
    <div className={classes.grid}>
      {inputs.map((input) => {
        const value = values[input.id];
        const isLongText = input.fieldType === TEMPLATE_CREATION_INPUT_TYPE.LONG_TEXT;
        const isProductMultiReference = input.fieldType === TEMPLATE_CREATION_INPUT_TYPE.PRODUCT_MULTI_REFERENCE;
        const isFullWidth = isLongText || isProductMultiReference;
        const showError = input.required && (showAllErrors || touched[input.id]) && !isInputProvided(value);
        const validationMessage = showError ? `${input.label} is required.` : undefined;

        return (
          <Field
            key={input.id}
            className={isFullWidth ? classes.fullWidth : undefined}
            label={input.label}
            required={input.required}
            hint={input.helpText}
            validationState={showError ? 'error' : 'none'}
            validationMessage={validationMessage}
          >
            {isProductMultiReference ? (
              <Dropdown
                multiselect
                placeholder={input.placeholder ?? 'Select products'}
                selectedOptions={asEntityReferences(value).map((reference) => reference.id)}
                value={asEntityReferences(value)
                  .map((reference) => reference.label)
                  .join(', ')}
                onOptionSelect={(_, data) => {
                  const selected = data.selectedOptions
                    .map((id) => MOCK_PRODUCTS.find((candidate) => candidate.id === id))
                    .filter((product): product is (typeof MOCK_PRODUCTS)[number] => Boolean(product))
                    .map((product) => ({ id: product.id, label: product.name }));
                  onChange(input.id, selected);
                }}
                onBlur={() => onBlur(input.id)}
              >
                {MOCK_PRODUCTS.map((product) => (
                  <Option key={product.id} value={product.id} text={product.name}>
                    {product.name}
                  </Option>
                ))}
              </Dropdown>
            ) : input.fieldType === TEMPLATE_CREATION_INPUT_TYPE.PRODUCT_REFERENCE ? (
              <Dropdown
                placeholder={input.placeholder ?? 'Select a product'}
                selectedOptions={value && typeof value === 'object' && !Array.isArray(value) ? [value.id] : []}
                value={value && typeof value === 'object' && !Array.isArray(value) ? value.label : ''}
                onOptionSelect={(_, data) => {
                  const product = MOCK_PRODUCTS.find((candidate) => candidate.id === data.optionValue);
                  onChange(input.id, product ? { id: product.id, label: product.name } : null);
                }}
                onBlur={() => onBlur(input.id)}
              >
                {MOCK_PRODUCTS.map((product) => (
                  <Option key={product.id} value={product.id} text={product.name}>
                    {product.name}
                  </Option>
                ))}
              </Dropdown>
            ) : input.fieldType === TEMPLATE_CREATION_INPUT_TYPE.SINGLE_CHOICE ? (
              <Dropdown
                placeholder={input.placeholder ?? 'Select an option'}
                selectedOptions={typeof value === 'string' && value ? [value] : []}
                value={asText(value)}
                onOptionSelect={(_, data) => onChange(input.id, (data.optionValue as string) ?? null)}
                onBlur={() => onBlur(input.id)}
              >
                {(input.options ?? []).map((option) => (
                  <Option key={option} value={option} text={option}>
                    {option}
                  </Option>
                ))}
              </Dropdown>
            ) : isLongText ? (
              <Textarea
                value={asText(value)}
                placeholder={input.placeholder}
                resize="vertical"
                onChange={(_, data) => onChange(input.id, data.value)}
                onBlur={() => onBlur(input.id)}
              />
            ) : (
              <Input
                value={asText(value)}
                placeholder={input.placeholder}
                onChange={(_, data) => onChange(input.id, data.value)}
                onBlur={() => onBlur(input.id)}
              />
            )}
          </Field>
        );
      })}
    </div>
  );
};
